<template>
    <div>
        <div class="d-flex mb-4 align-center">
            <h4 class="text-h6 text-capitalize">API key:</h4>
            <div v-if="settings.apiKey" class="ml-4">{{ settings.apiKey || '' }}</div>
            <AccessToApiModal v-model="isShowInfoModal">
                <template v-slot:activator>
                    <v-btn
                        size="small"
                        icon="mdi-information-outline"
                        variant="text"
                        color="primary"
                        class="ml-4"
                        data-cy="is-show-info-modal"
                        @click="isShowInfoModal = true"
                    />
                </template>
            </AccessToApiModal>
        </div>
        <div v-if="isEditApi" class="pb-6">
            <v-btn color="primary" data-cy="generate-api-key" @click="generateApiKey">
                {{ $t('generate') }}
            </v-btn>
        </div>
        <v-form ref="form" v-model="valid" data-cy="settings-form">
            <v-row no-gutters class="align-center pb-6">
                <v-checkbox
                    v-model="settings.exportEnabled"
                    :label="$t('api.export_data')"
                    density="compact"
                    color="primary"
                    data-cy="is-export-enabled"
                    :disabled="!isEditApi"
                    hide-details
                />
                <v-btn
                    v-if="isEditApi && settings.exportEnabled"
                    size="small"
                    icon="mdi-plus"
                    variant="text"
                    color="primary"
                    class="ml-4"
                    data-cy="add-row"
                    :disabled="!valid"
                    @click="addRow"
                />
            </v-row>
            <v-row v-for="setting in settings.externalSystemExportSetting" :key="setting.id" dense class="pb-4">
                <v-col cols="12" md="2" sm="6">
                    <v-text-field
                        v-model="setting.name"
                        :label="`${$t('name')}*`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        :data-cy="`name-${setting.id}`"
                        :disabled="!isEditApi"
                        :rules="[requiredRule('name')]"
                    />
                </v-col>
                <v-col cols="12" md="2" sm="6">
                    <v-text-field
                        v-model="setting.url"
                        label="URL*"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        :data-cy="`url-${setting.id}`"
                        :disabled="!isEditApi"
                        :rules="[requiredRule('api.url')]"
                    />
                </v-col>
                <v-col cols="12" md="5" sm="12">
                    <v-row dense class="align-center pl-1">
                        <div class="text-subtitle-1 font-italic">{{ $t('api.export_info') }}</div>
                        <v-btn
                            v-if="isEditApi"
                            size="small"
                            icon="mdi-close"
                            variant="text"
                            color="error"
                            class="ml-auto"
                            :data-cy="`remove-${setting.id}`"
                            @click="removeRow(setting.id)"
                        />
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <div class="text-subtitle-1">{{ $t('api.methods') }}</div>
                            <div v-for="method in setting.exportActions" :key="method.exportAction">
                                <v-checkbox
                                    v-model="method.enabled"
                                    :label="$t(`api.methodTypes.${method.exportAction}`)"
                                    density="compact"
                                    color="primary"
                                    hide-details
                                    :data-cy="method.exportAction"
                                    :disabled="!isEditApi"
                                />
                            </div>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <div class="text-subtitle-1">{{ $t('api.format') }}</div>
                            <v-radio-group v-model="setting.exportContentType" density="compact" hide-details>
                                <v-radio
                                    label="JSON"
                                    value="JSON"
                                    color="primary"
                                    data-cy="JSON"
                                    :disabled="!isEditApi"
                                />
                                <v-radio label="XML" value="XML" color="primary" data-cy="XML" :disabled="!isEditApi" />
                            </v-radio-group>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <div class="text-subtitle-1">{{ $t('api.protocol') }}</div>
                            <v-radio-group v-model="setting.exportProtocolType" density="compact" hide-details>
                                <v-radio
                                    label="HTTP"
                                    value="HTTP"
                                    color="primary"
                                    data-cy="HTTP"
                                    :disabled="!isEditApi"
                                />
                                <v-radio
                                    label="SOAP"
                                    value="SOAP"
                                    color="primary"
                                    data-cy="SOAP"
                                    :disabled="!isEditApi"
                                />
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
            <v-row no-gutters class="gc-4">
                <v-btn v-if="!isEditApi" data-cy="edit" @click="isEditApi = true">
                    {{ $t('edit') }}
                </v-btn>
                <v-btn v-if="isEditApi" data-cy="save" :disabled="isLoading || !valid" @click="save">
                    {{ $t('save') }}
                </v-btn>
                <v-btn v-if="isEditApi" data-cy="cancel" :disabled="isLoading" variant="text" @click="cancel">
                    {{ $t('cancel') }}
                </v-btn>
            </v-row>
        </v-form>
    </div>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {nanoid} from 'nanoid'

    const props = defineProps<{
        companyId: number
    }>()

    const initSettings = (): SettingsType => {
        return {
            apiKey: '',
            exportEnabled: false,
            externalSystemExportSetting: [],
        }
    }

    type SettingsType = {
        apiKey: string
        exportEnabled: boolean
        externalSystemExportSetting: {
            name?: string
            url?: string
            exportProtocolType?: string
            exportContentType?: string
            exportActions?: {exportAction: string; enabled: boolean}[]
            id?: string
        }[]
    }

    const isLoading = ref<boolean>(false)
    const isEditApi = ref<boolean>(false)
    const isShowInfoModal = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const settings = ref<SettingsType>(initSettings())

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await getSettings()
    })

    async function getSettings(): Promise<void> {
        try {
            isLoading.value = true
            settings.value = await $api.companiesSettings.get(props.companyId)
            settings.value.externalSystemExportSetting = settings.value.externalSystemExportSetting.map(item =>
                Object.assign(item, {id: nanoid()})
            )
        } catch (e) {
            settings.value = initSettings()
        } finally {
            isLoading.value = false
        }
    }

    async function generateApiKey(): Promise<void> {
        try {
            const resp = await $api.generateKey.get(props.companyId)
            settings.value.apiKey = resp.apiKey
        } catch (e) {
            settings.value = initSettings()
        }
    }

    async function save(): Promise<void> {
        try {
            isLoading.value = true
            settings.value.externalSystemExportSetting = settings.value.externalSystemExportSetting.map(item => {
                delete item.id
                return item
            })
            settings.value = await $api.companiesSettings.post(props.companyId, settings.value)
            settings.value.externalSystemExportSetting = settings.value.externalSystemExportSetting.map(item =>
                Object.assign(item, {id: nanoid()})
            )
            $toast.success(t('success_message.saved_successfully'))
            isEditApi.value = false
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || t('data_update_fail'))
        } finally {
            isLoading.value = false
        }
    }

    function addRow(): void {
        const methods = ['COMPANIES', 'PERSONS', 'ORDER', 'INVOICE', 'PAYMENT', 'CUSTOM_FIELDS', 'HOTEL_PENALTIES']
        const exportActions = methods.map(item => ({
            exportAction: item,
            enabled: true,
        }))
        settings.value.externalSystemExportSetting.push({
            name: '',
            url: '',
            exportProtocolType: 'HTTP',
            exportContentType: 'JSON',
            exportActions,
            id: nanoid(),
        })
    }

    function removeRow(id: string): void {
        settings.value.externalSystemExportSetting = settings.value.externalSystemExportSetting.filter(
            item => id !== item.id
        )
    }

    function cancel(): void {
        isEditApi.value = false
        getSettings()
    }
</script>
